import React, { useState, useEffect, useRef } from 'react';

type Props = {
  cursorIconDropped: boolean;
  setCursorIconDropped: Function;
  isCursorIconDuplicationVisible: boolean;
  setIsCursorIconDuplicationVisible: Function;
  initCursorPosition: { x: number; y: number };
};

const CursorIcon = ({
  cursorIconDropped,
  setCursorIconDropped,
  isCursorIconDuplicationVisible,
  setIsCursorIconDuplicationVisible,
  initCursorPosition,
}: Props) => {
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [initialOffset, setInitialOffset] = useState({ x: 0, y: 0 });
  const handleMouseMove = useRef<any>(null);

  useEffect(() => {
    setCursorPosition(initCursorPosition);
  }, [initCursorPosition]);

  useEffect(() => {
    if (cursorIconDropped) {
      window.removeEventListener('mousemove', handleMouseMove.current);
      return;
    }

    handleMouseMove.current = (event: MouseEvent) => {
      setCursorPosition({ x: event.clientX, y: event.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove.current);

    return () =>
      window.removeEventListener('mousemove', handleMouseMove.current);
  }, [cursorIconDropped]);

  const handleIconClick = (event: React.MouseEvent) => {
    setCursorIconDropped(false);
    setIsCursorIconDuplicationVisible(true);
    setInitialOffset({ x: event.clientX, y: event.clientY });
    setCursorPosition({ x: event.clientX, y: event.clientY });
  };

  const iconStyle: React.CSSProperties = !cursorIconDropped
    ? {
        position: 'fixed',
        top: cursorPosition.y - initialOffset.y - 51,
        left: cursorPosition.x - initialOffset.x - 30,
        pointerEvents: 'none',
      }
    : {
        position: 'fixed',
        top: -51,
        left: -30,
        cursor: 'pointer',
      };

  const duplicationStyle: React.CSSProperties = {
    position: 'fixed',
    top: cursorPosition.y - 51,
    left: cursorPosition.x - 30,
    pointerEvents: 'none',
    display: isCursorIconDuplicationVisible ? 'block' : 'none',
  };

  return (
    <div>
      <div
        id="cursorIcon"
        style={{
          ...iconStyle,
          display: !isCursorIconDuplicationVisible ? 'block' : 'none',
          zIndex: 999,
        }}
        onClick={handleIconClick}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="68"
          height="72"
          viewBox="0 0 68 72"
          fill="none"
        >
          <g filter="url(#filter0_dd_6228_3423)">
            <rect x="22" y="9" width="25" height="25" fill="#fff" />
            <path
              d="M31.6364 42C27.7333 38.6788 24.8182 35.5939 22.8909 32.7455C20.9636 29.897 20 27.2606 20 24.8364C20 21.2 21.1697 18.303 23.5091 16.1455C25.8485 13.9879 28.5576 12.9091 31.6364 12.9091H32.3636C32.6061 12.9091 32.8485 12.9333 33.0909 12.9818V15.9273C32.8485 15.8788 32.6121 15.8485 32.3818 15.8364C32.1515 15.8242 31.903 15.8182 31.6364 15.8182C29.1879 15.8182 27.1212 16.6606 25.4364 18.3455C23.7515 20.0303 22.9091 22.1939 22.9091 24.8364C22.9091 26.5576 23.6242 28.5273 25.0545 30.7455C26.4848 32.9636 28.6788 35.4303 31.6364 38.1455C34.5939 35.4303 36.7879 32.9636 38.2182 30.7455C39.6485 28.5273 40.3636 26.5576 40.3636 24.8364V24.5455H43.2727V24.8364C43.2727 27.2606 42.3091 29.897 40.3818 32.7455C38.4545 35.5939 35.5394 38.6788 31.6364 42ZM31.6364 27.4545C32.4364 27.4545 33.1212 27.1697 33.6909 26.6C34.2606 26.0303 34.5455 25.3455 34.5455 24.5455C34.5455 23.7455 34.2606 23.0606 33.6909 22.4909C33.1212 21.9212 32.4364 21.6364 31.6364 21.6364C30.8364 21.6364 30.1515 21.9212 29.5818 22.4909C29.0121 23.0606 28.7273 23.7455 28.7273 24.5455C28.7273 25.3455 29.0121 26.0303 29.5818 26.6C30.1515 27.1697 30.8364 27.4545 31.6364 27.4545ZM40.3636 21.6364H43.2727V17.2727H47.6364V14.3636H43.2727V10H40.3636V14.3636H36V17.2727H40.3636V21.6364Z"
              fill="#4E73C3"
            />
            <path
              d="M36.115 10.3636C35.5498 9.71071 34.773 9.23901 33.8754 9.0595C33.3798 8.96038 32.8746 8.90909 32.3636 8.90909H31.6364C27.5159 8.90909 23.8479 10.3915 20.7972 13.2051C17.5161 16.2312 16 20.2584 16 24.8364C16 28.3296 17.3829 31.7427 19.578 34.987C21.7809 38.2429 24.9863 41.5934 29.0441 45.0464C30.5384 46.3179 32.7343 46.3179 34.2286 45.0464C38.2864 41.5934 41.4918 38.2429 43.6948 34.987C45.8898 31.7427 47.2727 28.3296 47.2727 24.8364V24.5455C47.2727 24.0323 47.1761 23.5417 47 23.0909C47.1761 22.6401 47.2727 22.1495 47.2727 21.6364V21.2727H47.6364C49.8455 21.2727 51.6364 19.4819 51.6364 17.2727V14.3636C51.6364 12.1545 49.8455 10.3636 47.6364 10.3636H47.2727V10C47.2727 7.79086 45.4819 6 43.2727 6H40.3636C38.1545 6 36.3636 7.79086 36.3636 10V10.3636H36.115ZM31.6364 32.5518C31.2525 32.1474 30.8945 31.7547 30.5614 31.3738C30.9121 31.4273 31.271 31.4545 31.6364 31.4545C32.0017 31.4545 32.3606 31.4273 32.7114 31.3738C32.3783 31.7547 32.0202 32.1474 31.6364 32.5518Z"
              stroke="white"
              stroke-width="8"
              stroke-linejoin="round"
            />
          </g>
          <defs>
            <filter
              id="filter0_dd_6228_3423"
              x="0"
              y="0"
              width="67.6362"
              height="72"
              filterUnits="userSpaceOnUse"
              color-interpolation-filters="sRGB"
            >
              <feFlood flood-opacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feMorphology
                radius="2"
                operator="erode"
                in="SourceAlpha"
                result="effect1_dropShadow_6228_3423"
              />
              <feOffset dy="4" />
              <feGaussianBlur stdDeviation="3" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_6228_3423"
              />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feMorphology
                radius="3"
                operator="erode"
                in="SourceAlpha"
                result="effect2_dropShadow_6228_3423"
              />
              <feOffset dy="10" />
              <feGaussianBlur stdDeviation="7.5" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
              />
              <feBlend
                mode="normal"
                in2="effect1_dropShadow_6228_3423"
                result="effect2_dropShadow_6228_3423"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect2_dropShadow_6228_3423"
                result="shape"
              />
            </filter>
          </defs>
        </svg>
      </div>
      <div id="cursorIconDuplication" style={duplicationStyle}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="68"
          height="72"
          viewBox="0 0 68 72"
          fill="none"
        >
          <g filter="url(#filter0_dd_6228_3423)">
            <rect x="22" y="9" width="25" height="25" fill="#fff" />
            <path
              d="M31.6364 42C27.7333 38.6788 24.8182 35.5939 22.8909 32.7455C20.9636 29.897 20 27.2606 20 24.8364C20 21.2 21.1697 18.303 23.5091 16.1455C25.8485 13.9879 28.5576 12.9091 31.6364 12.9091H32.3636C32.6061 12.9091 32.8485 12.9333 33.0909 12.9818V15.9273C32.8485 15.8788 32.6121 15.8485 32.3818 15.8364C32.1515 15.8242 31.903 15.8182 31.6364 15.8182C29.1879 15.8182 27.1212 16.6606 25.4364 18.3455C23.7515 20.0303 22.9091 22.1939 22.9091 24.8364C22.9091 26.5576 23.6242 28.5273 25.0545 30.7455C26.4848 32.9636 28.6788 35.4303 31.6364 38.1455C34.5939 35.4303 36.7879 32.9636 38.2182 30.7455C39.6485 28.5273 40.3636 26.5576 40.3636 24.8364V24.5455H43.2727V24.8364C43.2727 27.2606 42.3091 29.897 40.3818 32.7455C38.4545 35.5939 35.5394 38.6788 31.6364 42ZM31.6364 27.4545C32.4364 27.4545 33.1212 27.1697 33.6909 26.6C34.2606 26.0303 34.5455 25.3455 34.5455 24.5455C34.5455 23.7455 34.2606 23.0606 33.6909 22.4909C33.1212 21.9212 32.4364 21.6364 31.6364 21.6364C30.8364 21.6364 30.1515 21.9212 29.5818 22.4909C29.0121 23.0606 28.7273 23.7455 28.7273 24.5455C28.7273 25.3455 29.0121 26.0303 29.5818 26.6C30.1515 27.1697 30.8364 27.4545 31.6364 27.4545ZM40.3636 21.6364H43.2727V17.2727H47.6364V14.3636H43.2727V10H40.3636V14.3636H36V17.2727H40.3636V21.6364Z"
              fill="#4E73C3"
            />
            <path
              d="M36.115 10.3636C35.5498 9.71071 34.773 9.23901 33.8754 9.0595C33.3798 8.96038 32.8746 8.90909 32.3636 8.90909H31.6364C27.5159 8.90909 23.8479 10.3915 20.7972 13.2051C17.5161 16.2312 16 20.2584 16 24.8364C16 28.3296 17.3829 31.7427 19.578 34.987C21.7809 38.2429 24.9863 41.5934 29.0441 45.0464C30.5384 46.3179 32.7343 46.3179 34.2286 45.0464C38.2864 41.5934 41.4918 38.2429 43.6948 34.987C45.8898 31.7427 47.2727 28.3296 47.2727 24.8364V24.5455C47.2727 24.0323 47.1761 23.5417 47 23.0909C47.1761 22.6401 47.2727 22.1495 47.2727 21.6364V21.2727H47.6364C49.8455 21.2727 51.6364 19.4819 51.6364 17.2727V14.3636C51.6364 12.1545 49.8455 10.3636 47.6364 10.3636H47.2727V10C47.2727 7.79086 45.4819 6 43.2727 6H40.3636C38.1545 6 36.3636 7.79086 36.3636 10V10.3636H36.115ZM31.6364 32.5518C31.2525 32.1474 30.8945 31.7547 30.5614 31.3738C30.9121 31.4273 31.271 31.4545 31.6364 31.4545C32.0017 31.4545 32.3606 31.4273 32.7114 31.3738C32.3783 31.7547 32.0202 32.1474 31.6364 32.5518Z"
              stroke="white"
              stroke-width="8"
              stroke-linejoin="round"
            />
          </g>
          <defs>
            <filter
              id="filter0_dd_6228_3423"
              x="0"
              y="0"
              width="67.6362"
              height="72"
              filterUnits="userSpaceOnUse"
              color-interpolation-filters="sRGB"
            >
              <feFlood flood-opacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feMorphology
                radius="2"
                operator="erode"
                in="SourceAlpha"
                result="effect1_dropShadow_6228_3423"
              />
              <feOffset dy="4" />
              <feGaussianBlur stdDeviation="3" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_6228_3423"
              />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feMorphology
                radius="3"
                operator="erode"
                in="SourceAlpha"
                result="effect2_dropShadow_6228_3423"
              />
              <feOffset dy="10" />
              <feGaussianBlur stdDeviation="7.5" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
              />
              <feBlend
                mode="normal"
                in2="effect1_dropShadow_6228_3423"
                result="effect2_dropShadow_6228_3423"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect2_dropShadow_6228_3423"
                result="shape"
              />
            </filter>
          </defs>
        </svg>
      </div>
    </div>
  );
};

export default CursorIcon;
